// Standing reminder that the UI is being rendered as a lesser role.
//
// "View as" is a display clamp, not a security boundary: the token still
// carries system-admin claims, so writes keep working. That is deliberate —
// the point is to see what a manager or user sees *and still be able to fix
// what you find* — but it makes forgetting the mode dangerous in the other
// direction. Without a standing marker, "the Settings menu is missing" reads
// as a bug rather than as the simulation doing its job. Hence a bar that
// cannot be dismissed, only exited.
import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuth } from '../contexts/AuthContext';
import { TRAK } from '../theme/trakTokens';

const ROLE_LABELS = {
  admin: 'workspace admin',
  manager: 'manager',
  user: 'user',
};

export default function ViewAsBanner() {
  const { simulatedRole, setSimulatedRole, actualSubscriberRole } = useAuth();

  if (!simulatedRole) return null;

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 2,
        py: 0.75,
        // The token set has no warning family (only success and danger), and
        // this is neither: it is a mode indicator. Amber reads as "temporary
        // state" without borrowing the red of an error.
        bgcolor: '#fff4e5',
        borderBottom: '1px solid rgba(13,51,32,.12)',
      }}
    >
      <VisibilityIcon sx={{ fontSize: 17, color: TRAK.ink }} />
      <Typography sx={{ font: `500 12.5px/1.3 ${TRAK.sans}`, color: TRAK.ink }}>
        Viewing as <strong>{ROLE_LABELS[simulatedRole] || simulatedRole}</strong>
        {actualSubscriberRole && actualSubscriberRole !== simulatedRole && (
          <Box component="span" sx={{ color: TRAK.muted }}>
            {' '}(your own access is unchanged, and edits still save)
          </Box>
        )}
      </Typography>
      <Box sx={{ flex: 1 }} />
      <Button
        size="small"
        onClick={() => setSimulatedRole(null)}
        sx={{ font: `600 11.5px/1 ${TRAK.sans}`, color: TRAK.green }}
      >
        Exit
      </Button>
    </Box>
  );
}
